/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * phoneIndexLookup — the contact-sync match engine.
 *
 * Reads /phoneIndex/{e164} one key at a time. The /phoneIndex rule
 * deliberately allows single-key reads but not parent enumeration; this
 * helper respects that contract by doing N independent point reads, not a
 * range query.
 *
 * Inputs are deduped and the viewer's own uid is filtered out of the
 * result map — typing your own number into the device's address book
 * should not surface you as a match against yourself.
 *
 * Concurrency is chunked. The Firebase JS SDK multiplexes RTDB reads over
 * a single WebSocket, but firing 500 reads at once still creates pointless
 * head-of-line jitter on slower connections; CHUNK = 20 keeps perceived
 * latency tight on a typical iOS address book without starving the bus.
 */

import { get, ref, type Database } from "firebase/database";

const DEFAULT_CHUNK_SIZE = 20;

export interface LookupOptions {
  /** Override chunk size; only useful in tests. */
  chunkSize?: number;
  /** Injected for tests — defaults to firebase/database `get` + `ref`. */
  read?: (database: Database, path: string) => Promise<string | null>;
}

async function defaultRead(
  database: Database,
  path: string,
): Promise<string | null> {
  const snap = await get(ref(database, path));
  if (!snap.exists()) return null;
  const v = snap.val();
  return typeof v === "string" ? v : null;
}

/**
 * Look up a single E.164 against /phoneIndex. Returns the matched uid or
 * null. Caller is responsible for validating the input is E.164 — passing
 * a national-format string here silently misses because /phoneIndex is
 * keyed on the E.164 string itself.
 */
export async function lookupPhoneIndex(
  database: Database,
  e164: string,
  options: LookupOptions = {},
): Promise<string | null> {
  const read = options.read ?? defaultRead;
  return read(database, `phoneIndex/${e164}`);
}

/**
 * Batch-resolve E.164 strings to uids. Dedupes input, skips the viewer's
 * own uid in the output, returns a map keyed by E.164. Empty input
 * resolves to an empty map without hitting RTDB.
 */
export async function batchLookupPhoneIndex(
  database: Database,
  e164s: readonly string[],
  viewerUid: string | null,
  options: LookupOptions = {},
): Promise<Map<string, string>> {
  const unique = Array.from(new Set(e164s.filter((s) => s.startsWith("+"))));
  const result = new Map<string, string>();
  if (unique.length === 0) return result;

  const chunkSize = Math.max(1, options.chunkSize ?? DEFAULT_CHUNK_SIZE);
  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const reads = chunk.map(async (e164) => {
      const uid = await lookupPhoneIndex(database, e164, options);
      return [e164, uid] as const;
    });
    const settled = await Promise.all(reads);
    for (const [e164, uid] of settled) {
      if (!uid) continue;
      if (viewerUid && uid === viewerUid) continue;
      result.set(e164, uid);
    }
  }
  return result;
}
